import {
    attrsToBuilderProps,
    getShortAnswerToolLabelByProperties,
    parseAssessmentOptionsJson,
} from '@/components/tiptap-editor/tiptap-extension/short-answer-extension/tool-settings';
import { isPlainObject } from '@/lib/primitive-utils';
import type { JSONContent } from '@tiptap/react';

const LEGACY_ANSWER_ATTR_KEYS = [
    'assessmentOptionsJson',
    'notation',
    'lines',
    'columns',
    'obeySpace',
    'obeyCase',
    'ignoreEnd',
    'obeyQuote',
    'obeyLinear',
    'renderAsHTML',
] as const;

function liftLegacyFlatAttrs(raw: Record<string, unknown>): Record<string, unknown> {
    const assessmentOptions: Record<string, unknown> = isPlainObject(raw.assessmentOptions)
        ? { ...(raw.assessmentOptions as Record<string, unknown>) }
        : {};

    if (typeof raw.assessmentOptionsJson === 'string') {
        Object.assign(assessmentOptions, parseAssessmentOptionsJson(raw.assessmentOptionsJson));
    }

    const setDefined = (key: string, v: unknown) => {
        if (v !== undefined && v !== null) {
            assessmentOptions[key] = v;
        }
    };

    if (raw.notation !== undefined && raw.notation !== null && raw.notation !== '') {
        setDefined('notation', raw.notation);
    }
    if (raw.lines !== undefined && raw.lines !== null && raw.lines !== '') {
        const n = Number(raw.lines);
        if (!Number.isNaN(n)) {
            setDefined('lines', n);
        }
    }
    if (raw.columns !== undefined && raw.columns !== null && raw.columns !== '') {
        const n = Number(raw.columns);
        if (!Number.isNaN(n)) {
            setDefined('columns', n);
        }
    }
    if (raw.obeySpace !== undefined) {
        setDefined('obeyspace', Boolean(raw.obeySpace));
    }
    if (raw.obeyCase !== undefined) {
        setDefined('obeycase', Boolean(raw.obeyCase));
    }
    if (raw.ignoreEnd !== undefined && raw.ignoreEnd !== null) {
        setDefined('ignoreend', String(raw.ignoreEnd));
    }
    if (raw.obeyQuote !== undefined) {
        setDefined('obeyquote', Boolean(raw.obeyQuote));
    }
    if (raw.obeyLinear !== undefined) {
        setDefined('obeylinear', Boolean(raw.obeyLinear));
    }

    return assessmentOptions;
}

function hasLegacyAnswerAttrs(raw: Record<string, unknown>): boolean {
    return LEGACY_ANSWER_ATTR_KEYS.some(key => key in raw);
}

function hasToolLabel(raw: Record<string, unknown>): boolean {
    const toolLabel = raw.toolLabel;
    return typeof toolLabel === 'string' && toolLabel !== '';
}

export function migrateShortAnswerAnswerAttrs(raw: Record<string, unknown>): Record<string, unknown> {
    if (hasToolLabel(raw)) {
        return raw;
    }

    const assessmentOptions = hasLegacyAnswerAttrs(raw)
        ? liftLegacyFlatAttrs(raw)
        : { ...((raw.assessmentOptions as Record<string, unknown>) ?? {}) };

    const assessmentType = (raw.assessmentType as string | null | undefined) ?? 'string';
    const toolLabel = getShortAnswerToolLabelByProperties(
        attrsToBuilderProps({
            assessmentType,
            assessmentOptions,
        }),
    );

    return {
        id: raw.id ?? null,
        answers: raw.answers ?? [],
        preferredIndex: raw.preferredIndex ?? null,
        toolLabel,
        assessmentType,
        assessmentOptions,
    };
}

/**
 * Migrate short answer answer nodes in JSON content to question set builder format.
 * @param doc - The ProseMirror JSON content to migrate.
 * @returns The migrated ProseMirror JSON content.
 */
export function migrateShortAnswerAnswerNodesInJson(doc: JSONContent): JSONContent {
    const cloned = structuredClone(doc);
    walkAndMigrateShortAnswerAnswerNodes(cloned);
    return cloned;
}

function walkAndMigrateShortAnswerAnswerNodes(node: JSONContent): void {
    if (node.type === 'shortAnswerAnswer' && node.attrs) {
        node.attrs = migrateShortAnswerAnswerAttrs(node.attrs as Record<string, unknown>);
    }
    for (const child of node.content ?? []) {
        walkAndMigrateShortAnswerAnswerNodes(child);
    }
}
